import React, { useState, useEffect } from 'react';
import { useConsultation } from '../../contexts/ConsultationContext';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import '../styles/ChatWithAstrologer.css';

/**
 * ChatWithAstrologer - Complete booking and chat flow
 * 1. Browse registered astrologers from database
 * 2. Click astrologer to see available slots
 * 3. Book a slot for consultation
 * 4. Auto-session creation at scheduled time
 * 5. Real-time chat with astrologer
 */
const ChatWithAstrologer = () => {
  console.log('ChatWithAstrologer component rendering');

  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [registeredAstrologers, setRegisteredAstrologers] = useState([]);
  const [loadingAstrologers, setLoadingAstrologers] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const consultationContext = useConsultation();
  const chatContext = useChat();

  const {
    selectedAstrologer,
    setSelectedAstrologer,
    currentConsultationId,
  } = consultationContext;

  const {
    isConnected,
    messages,
    connect,
    disconnect,
    sendMessage: sendChatMessage,
  } = chatContext;

  // Fetch registered astrologers from database
  useEffect(() => {
    const fetchRegisteredAstrologers = async () => {
      try {
        setLoadingAstrologers(true);
        console.log('Fetching registered astrologers from database...');

        const response = await api.get('/astrologers');
        console.log('Astrologers fetched:', response.data);

        // Backend returns { astrologers: [...], pagination: {...} }
        if (response.data && response.data.astrologers) {
          setRegisteredAstrologers(response.data.astrologers);
          console.log('✅ Loaded', response.data.astrologers.length, 'astrologers');
        } else {
          console.warn('Unexpected API response format:', response.data);
          setRegisteredAstrologers([]);
        }
      } catch (error) {
        console.error('Error fetching astrologers:', error);
        setRegisteredAstrologers([]);
      } finally {
        setLoadingAstrologers(false);
      }
    };

    fetchRegisteredAstrologers();
  }, []);

  // Connect to chat when consultation is active
  useEffect(() => {
    if (currentConsultationId) {
      console.log('Connecting to chat with consultation ID:', currentConsultationId);
      connect(currentConsultationId);
    }

    return () => {
      if (currentConsultationId) {
        console.log('Disconnecting from chat');
        disconnect();
      }
    };
  }, [currentConsultationId, connect, disconnect]);

  const handleAstrologerClick = (astrologer) => {
    console.log('Astrologer clicked:', astrologer);

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Navigate to new calendar v3 booking page
    navigate(`/book-consultation/${astrologer.id}`);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !currentConsultationId) {
      console.log('Cannot send message:', { message, currentConsultationId });
      return;
    }

    console.log('Sending message:', message);
    try {
      sendChatMessage({
        consultationId: currentConsultationId,
        content: message,
        sender: 'user',
        timestamp: new Date().toISOString(),
      });
      setMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleBackToList = () => {
    console.log('Going back to astrologer list');
    setSelectedAstrologer(null);
    disconnect();
  };

  const filteredAstrologers = registeredAstrologers.filter(astrologer => {
    const matchesSearch =
      (astrologer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (astrologer.specialization || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      activeFilter === 'All' ||
      (activeFilter === 'Online' && astrologer.is_online) ||
      astrologer.specialization === activeFilter;

    return matchesSearch && matchesFilter;
  });

  const handleLoginClick = (e) => {
    e.preventDefault();
    console.log('Login button clicked, navigating to /login');
    navigate('/login');
  };

  if (!isAuthenticated) {
    console.log('User not authenticated, showing login prompt');
    return (
      <div className="chat-with-astrologer">
        <div className="login-prompt">
          <h2>Please Login to Chat with Astrologers</h2>
          <p>You need to be logged in to book consultation slots with our astrologers.</p>
          <button onClick={handleLoginClick}>Login</button>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/login" style={{ color: '#3498db', textDecoration: 'underline' }}>
              Or click here to login
            </Link>
          </p>
        </div>
      </div>
    );
  }

  console.log('Rendering main component content');
  return (
    <div className="chat-with-astrologer">
      <h1>Book Consultation with Astrologer</h1>

      {loadingAstrologers ? (
        <div className="loading">Loading astrologers...</div>
      ) : currentConsultationId && selectedAstrologer ? (
        // Active chat session
        <div className="chat-window">
          <div className="chat-header">
            <h2>{selectedAstrologer.user?.name || selectedAstrologer.name}</h2>
            <div className="connection-status">
              {isConnected ? (
                <span className="online">● Connected</span>
              ) : (
                <span className="offline">● Connecting...</span>
              )}
            </div>
            <button className="back-btn" onClick={handleBackToList}>Back to List</button>
          </div>

          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="welcome-message">
                <p>Welcome to your chat with {selectedAstrologer.user?.name || selectedAstrologer.name}</p>
                <p>Start your conversation by sending a message.</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`message ${msg.sender === 'user' ? 'user-message' : 'astrologer-message'}`}>
                  {msg.content}
                  <span className="message-time">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="chat-input">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              disabled={!isConnected}
            />
            <button
              onClick={handleSendMessage}
              disabled={!isConnected}
            >
              Send
            </button>
          </div>
        </div>
      ) : (
        // Astrologer browsing and booking
        <>
          <div className="filters">
            <div className="filter-buttons">
              <button
                className={activeFilter === 'All' ? 'active' : ''}
                onClick={() => setActiveFilter('All')}
              >
                All
              </button>
              <button
                className={activeFilter === 'Online' ? 'active' : ''}
                onClick={() => setActiveFilter('Online')}
              >
                Online Now
              </button>
              <button
                className={activeFilter === 'Vedic Astrology' ? 'active' : ''}
                onClick={() => setActiveFilter('Vedic Astrology')}
              >
                Vedic Astrology
              </button>
              <button
                className={activeFilter === 'Numerology' ? 'active' : ''}
                onClick={() => setActiveFilter('Numerology')}
              >
                Numerology
              </button>
            </div>
            <input
              type="text"
              className="search-input"
              placeholder="Search astrologers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="astrologers-list">
            {filteredAstrologers.length === 0 ? (
              <div className="no-astrologers">
                <p>No astrologers found. Please adjust your search or filters.</p>
              </div>
            ) : (
              filteredAstrologers.map(astrologer => (
                <div key={astrologer.id} className="astrologer-card">
                  <div className="astrologer-info">
                    <img
                      src={astrologer.profile_image || 'https://via.placeholder.com/100'}
                      alt={astrologer.name || 'Astrologer'}
                      className="astrologer-image"
                    />
                    <div className="astrologer-details">
                      <h3>{astrologer.name || 'Unknown'}</h3>
                      <p className="specialization">{astrologer.specialization || 'General Astrology'}</p>
                      <div className="rating">
                        <span>⭐ {astrologer.rating || '5.0'}</span>
                        <span>{astrologer.experience_years ? `${astrologer.experience_years} years exp` : 'New'}</span>
                      </div>
                      <p className="rate">₹{astrologer.price_per_minute_chat || '20'}/min</p>
                      <div className="status">
                        {astrologer.is_online ? (
                          <span className="online">● Online</span>
                        ) : (
                          <span className="offline">● Offline</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="astrologer-actions">
                    <button
                      className="chat-btn"
                      onClick={() => handleAstrologerClick(astrologer)}
                    >
                      View Slots & Book
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWithAstrologer;
